import { useState, useEffect, useCallback } from "react";

// ─── Fallback photos shown before real images are scraped ─────────────────
const FALLBACK_IMAGES = {
  "Geta":       "https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=600&q=80",
  "Jypsiere":   "https://images.unsplash.com/photo-1591561954557-26941169b49e?w=600&q=80",
  "Petite":     "https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=600&q=80",
  "Halzan":     "https://assets.hermes.com/is/image/hermesproduct/halzan-25-bag--082027CC37-worn-1-0-0-800-800_g.jpg",
  "Videpoches": "https://assets.hermes.com/is/image/hermesproduct/hermes-videpoches-bag--086279CKS4-front-wm-1-0-0-800-800_g.jpg",
  "default":    "https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=600&q=80",
};

const getBagImage = (bag) => {
  if (bag.image_url) return bag.image_url;
  const key = Object.keys(FALLBACK_IMAGES).find(k =>
    k !== "default" && bag.name.toLowerCase().includes(k.toLowerCase())
  );
  return key ? FALLBACK_IMAGES[key] : FALLBACK_IMAGES["default"];
};

const statusConfig = {
  available:   { label: "AVAILABLE",   color: "#c8a96e", bg: "rgba(200,169,110,0.15)", dot: "#c8a96e", glow: "0 0 12px rgba(200,169,110,0.5)" },
  price_drop:  { label: "PRICE DROP",  color: "#7ec8a9", bg: "rgba(126,200,169,0.15)", dot: "#7ec8a9", glow: "0 0 12px rgba(126,200,169,0.5)" },
  unavailable: { label: "UNAVAILABLE", color: "#4a4540", bg: "rgba(60,55,50,0.3)",     dot: "#3a3530", glow: "none" },
};

// ─── Storage helpers (saves API URL between sessions) ─────────────────────
const STORAGE_KEY = "hermes_api_url";
const getSavedUrl = () => {
  try { return localStorage.getItem(STORAGE_KEY) || ""; } catch { return ""; }
};
const saveUrl = (url) => {
  try { localStorage.setItem(STORAGE_KEY, url); } catch {}
};

// ─── Setup screen ─────────────────────────────────────────────────────────
function SetupScreen({ onConnect }) {
  const [url, setUrl] = useState("");
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");

  const handleConnect = async () => {
    const clean = url.trim().replace(/\/$/, "");
    if (!clean) { setError("Please enter your Render URL"); return; }
    setTesting(true);
    setError("");
    try {
      const res = await fetch(`${clean}/health`, { signal: AbortSignal.timeout(8000) });
      if (!res.ok) throw new Error();
      saveUrl(clean);
      onConnect(clean);
    } catch {
      setError("Could not connect. Make sure your Render service is running and the URL is correct.");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh", background: "#0e0c0a",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'Cormorant Garamond', Georgia, serif", padding: 24,
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300&family=Josefin+Sans:wght@200;300;400&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes fadeIn { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
      `}</style>

      <div style={{ maxWidth: 520, width: "100%", animation: "fadeIn 0.5s ease" }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>👜</div>
          <h1 style={{ fontSize: 36, fontWeight: 300, color: "#e8e0d4", letterSpacing: 2, marginBottom: 8 }}>
            Hermès <span style={{ color: "#c8a96e", fontStyle: "italic" }}>Monitor</span>
          </h1>
          <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 9, letterSpacing: 4, color: "#444" }}>
            CONNECT YOUR BOT TO GET STARTED
          </div>
        </div>

        {/* Steps */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 36 }}>
          {[
            { n: "1", t: "Deploy the bot on Render.com", d: "Follow Part D of the deployment guide" },
            { n: "2", t: "Copy your Render URL", d: "It looks like: https://hermes-monitor-bot.onrender.com" },
            { n: "3", t: "Paste it below and click Connect", d: "The dashboard will sync automatically from then on" },
          ].map(step => (
            <div key={step.n} style={{
              display: "flex", gap: 16, alignItems: "flex-start",
              background: "#111009", border: "1px solid #1e1c18",
              padding: "14px 18px",
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                background: "#c8a96e", display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "'Josefin Sans', sans-serif", fontSize: 11, color: "#0e0c0a", fontWeight: 400,
              }}>{step.n}</div>
              <div>
                <div style={{ fontSize: 16, color: "#e8e0d4", marginBottom: 3 }}>{step.t}</div>
                <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 9, letterSpacing: 1, color: "#444" }}>{step.d}</div>
              </div>
            </div>
          ))}
        </div>

        {/* URL input */}
        <div style={{ display: "flex", gap: 0, marginBottom: 12 }}>
          <input
            value={url}
            onChange={e => { setUrl(e.target.value); setError(""); }}
            onKeyDown={e => e.key === "Enter" && handleConnect()}
            placeholder="https://your-app-name.onrender.com"
            style={{
              flex: 1, background: "#1a1612", border: "1px solid #2a2520",
              borderRight: "none", color: "#e8e0d4", padding: "12px 16px",
              fontFamily: "'Josefin Sans', sans-serif", fontSize: 11, letterSpacing: 1,
              outline: "none",
            }}
          />
          <button
            onClick={handleConnect}
            disabled={testing}
            style={{
              background: testing ? "#2a2520" : "#c8a96e",
              border: "none", cursor: testing ? "not-allowed" : "pointer",
              padding: "12px 24px", color: testing ? "#555" : "#0e0c0a",
              fontFamily: "'Josefin Sans', sans-serif", fontSize: 10, letterSpacing: 2,
              transition: "all 0.3s", whiteSpace: "nowrap",
            }}
          >
            {testing ? "TESTING…" : "CONNECT"}
          </button>
        </div>

        {error && (
          <div style={{
            background: "rgba(200,80,80,0.1)", border: "1px solid rgba(200,80,80,0.3)",
            padding: "10px 14px", color: "#c87070",
            fontFamily: "'Josefin Sans', sans-serif", fontSize: 10, letterSpacing: 1,
          }}>
            ⚠ {error}
          </div>
        )}

        <div style={{
          marginTop: 32, padding: "14px 18px",
          background: "#080706", border: "1px solid #1a1612",
          fontFamily: "'Josefin Sans', sans-serif", fontSize: 9, letterSpacing: 1, color: "#333",
          lineHeight: 1.8,
        }}>
          <div style={{ color: "#444", marginBottom: 6, letterSpacing: 2 }}>NOT DEPLOYED YET?</div>
          Download the deployment guide from your zip file and follow Part D — it takes about 10 minutes on Render.com (free).
        </div>
      </div>
    </div>
  );
}

// ─── Lightbox ─────────────────────────────────────────────────────────────
function Lightbox({ bag, onClose }) {
  const s = statusConfig[bag.status] || statusConfig.unavailable;
  const ph = bag.priceHistory || [];
  const drop = ph.length >= 2
    ? Math.round(((ph[0] - ph[ph.length - 1]) / ph[0]) * 100) : 0;

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(8,6,4,0.92)", display: "flex",
      alignItems: "center", justifyContent: "center",
      padding: 24, backdropFilter: "blur(8px)",
      animation: "fadeIn 0.2s ease",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "#0e0c0a", border: "1px solid #2a2520",
        maxWidth: 820, width: "100%", display: "flex",
        animation: "slideUp 0.3s ease",
        boxShadow: "0 40px 80px rgba(0,0,0,0.8)",
        maxHeight: "90vh", overflow: "hidden",
      }}>
        {/* Photo */}
        <div style={{ width: 360, flexShrink: 0, position: "relative", overflow: "hidden" }}>
          <img src={getBagImage(bag)} alt={bag.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", minHeight: 400 }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, transparent 50%, rgba(14,12,10,0.85) 100%)" }} />
          <div style={{ position: "absolute", top: 16, left: 16, background: s.bg, border: `1px solid ${s.color}`, padding: "4px 12px", backdropFilter: "blur(4px)" }}>
            <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 9, letterSpacing: 3, color: s.color }}>{s.label}</span>
          </div>
        </div>

        {/* Details */}
        <div style={{ flex: 1, padding: "32px 28px", display: "flex", flexDirection: "column", justifyContent: "space-between", overflowY: "auto" }}>
          <div>
            <button onClick={onClose} style={{ position: "absolute", top: 16, right: 16, background: "none", border: "none", cursor: "pointer", color: "#555", fontSize: 20 }}>✕</button>
            <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 8, letterSpacing: 4, color: "#555", marginBottom: 10 }}>HERMÈS PARIS</div>
            <h2 style={{ fontSize: 30, fontWeight: 300, color: "#e8e0d4", marginBottom: 6, letterSpacing: 1 }}>{bag.name}</h2>
            <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 9, letterSpacing: 2, color: "#555", marginBottom: 24 }}>
              {bag.material !== "—" ? bag.material.toUpperCase() : ""}{bag.material !== "—" && bag.color !== "—" ? " · " : ""}{bag.color !== "—" ? bag.color.toUpperCase() : ""}
            </div>

            {/* Price */}
            <div style={{ marginBottom: 20 }}>
              {bag.price ? (
                <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                  <span style={{ fontSize: 34, fontWeight: 300, color: bag.status === "price_drop" ? "#7ec8a9" : "#c8a96e" }}>
                    A${bag.price.toLocaleString()}
                  </span>
                  {drop > 0 && <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 9, color: "#7ec8a9" }}>↓{drop}% from peak</span>}
                </div>
              ) : (
                <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 10, color: "#3a3028", letterSpacing: 3 }}>PRICE UNAVAILABLE</div>
              )}
            </div>

            {/* Price history */}
            {ph.length > 1 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 8, letterSpacing: 3, color: "#555", marginBottom: 8 }}>PRICE HISTORY</div>
                <div style={{ display: "flex", gap: 4, alignItems: "flex-end", height: 44 }}>
                  {ph.map((p, i) => {
                    const max = Math.max(...ph), min = Math.min(...ph);
                    const h = 12 + Math.round(((p - min) / (max - min || 1)) * 32);
                    const isLast = i === ph.length - 1;
                    return (
                      <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                        <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 7, color: isLast ? s.color : "#3a3028" }}>
                          A${(p/1000).toFixed(1)}k
                        </div>
                        <div style={{ width: "100%", height: h, background: isLast ? s.color : "#2a2520", borderRadius: 1, boxShadow: isLast ? s.glow : "none" }} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Meta */}
            {[["Last checked", bag.lastSeen], ["Alert", bag.alertSet ? "Active" : "Off"]].map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #1a1612" }}>
                <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 9, letterSpacing: 2, color: "#555" }}>{k.toUpperCase()}</span>
                <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 9, color: "#e8e0d4" }}>{v}</span>
              </div>
            ))}
          </div>

          <a href={bag.url || "https://www.hermes.com/au"} target="_blank" rel="noopener noreferrer"
            style={{ display: "block", textAlign: "center", border: "1px solid #c8a96e", color: "#c8a96e", padding: "12px 0", fontFamily: "'Josefin Sans', sans-serif", fontSize: 9, letterSpacing: 3, textDecoration: "none", marginTop: 20 }}>
            VIEW ON HERMÈS.COM ↗
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── Main dashboard ────────────────────────────────────────────────────────
export default function HermesMonitor() {
  const [apiBase, setApiBase] = useState(getSavedUrl());
  const [bags, setBags] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [activeTab, setActiveTab] = useState("watchlist");
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState("—");
  const [scanProgress, setScanProgress] = useState(0);
  const [notification, setNotification] = useState(null);
  const [selectedBag, setSelectedBag] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);

  // ── Fetch live data from bot API ─────────────────────────────────────────
  const fetchData = useCallback(async (base) => {
    const url = base || apiBase;
    if (!url) return;
    try {
      const res = await fetch(`${url}/api/watchlist`, { signal: AbortSignal.timeout(10000) });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setBags(data.bags || []);
      if (data.lastScan) setLastScan(data.lastScan);
      setApiError(false);
    } catch {
      setApiError(true);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  const fetchAlerts = useCallback(async () => {
    if (!apiBase) return;
    try {
      const res = await fetch(`${apiBase}/api/alerts`, { signal: AbortSignal.timeout(8000) });
      if (!res.ok) return;
      setAlerts(await res.json());
    } catch {}
  }, [apiBase]);

  useEffect(() => {
    if (!apiBase) { setLoading(false); return; }
    fetchData();
    fetchAlerts();
    const iv = setInterval(() => { fetchData(); fetchAlerts(); }, 60000);
    return () => clearInterval(iv);
  }, [apiBase, fetchData, fetchAlerts]);

  const handleConnect = (url) => {
    setApiBase(url);
    setLoading(true);
    fetchData(url);
  };

  const handleDisconnect = () => {
    saveUrl("");
    setApiBase("");
    setBags([]);
    setAlerts([]);
    setLoading(true);
  };

  const triggerScan = useCallback(() => {
    setScanning(true); setScanProgress(0);
    let p = 0;
    const tick = setInterval(() => {
      p += Math.random() * 18 + 4;
      if (p >= 100) {
        clearInterval(tick); setScanning(false);
        setLastScan("Just now");
        fetchData();
        showNotification("Scan complete — data refreshed");
      }
      setScanProgress(Math.min(p, 100));
    }, 200);
  }, [fetchData]);

  const showNotification = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 4000);
  };

  const toggleAlert = (id, e) => {
    e.stopPropagation();
    setBags(prev => prev.map(b => b.id === id ? { ...b, alertSet: !b.alertSet } : b));
  };

  // ── Show setup screen if no API URL saved ────────────────────────────────
  if (!apiBase) return <SetupScreen onConnect={handleConnect} />;

  const availableCount = bags.filter(b => b.status !== "unavailable").length;
  const alertCount     = bags.filter(b => b.alertSet).length;

  return (
    <div style={{ minHeight: "100vh", background: "#0e0c0a", fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#e8e0d4" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300&family=Josefin+Sans:wght@200;300;400&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: #0e0c0a; } ::-webkit-scrollbar-thumb { background: #3a3028; border-radius: 2px; }
        .tab-btn { background: none; border: none; cursor: pointer; font-family: 'Josefin Sans', sans-serif; font-size: 10px; letter-spacing: 3px; text-transform: uppercase; padding: 10px 20px; color: #555; transition: color 0.3s; position: relative; }
        .tab-btn.active { color: #c8a96e; }
        .tab-btn.active::after { content: ''; position: absolute; bottom: 0; left: 20px; right: 20px; height: 1px; background: #c8a96e; }
        .tab-btn:hover { color: #9a8060; }
        .bag-card { background: #111009; border: 1px solid #1e1c18; border-radius: 2px; cursor: pointer; transition: border-color 0.3s, transform 0.25s, box-shadow 0.3s; position: relative; overflow: hidden; }
        .bag-card:hover { border-color: #3a3028; transform: translateY(-3px); box-shadow: 0 12px 40px rgba(0,0,0,0.6); }
        .bag-card.available  { border-top: 2px solid #c8a96e; }
        .bag-card.price_drop { border-top: 2px solid #7ec8a9; }
        .bag-card .photo-wrap { position: relative; overflow: hidden; height: 220px; }
        .bag-card .photo-wrap img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.6s ease; filter: brightness(0.88) saturate(0.9); }
        .bag-card:hover .photo-wrap img { transform: scale(1.04); filter: brightness(0.95) saturate(1.05); }
        .photo-overlay { position: absolute; inset: 0; background: linear-gradient(to bottom, transparent 40%, rgba(10,8,6,0.95) 100%); }
        .photo-status { position: absolute; top: 12px; left: 12px; padding: 3px 10px; font-family: 'Josefin Sans', sans-serif; font-size: 8px; letter-spacing: 2.5px; backdrop-filter: blur(6px); }
        .alert-toggle { width: 36px; height: 20px; border-radius: 10px; background: #2a2520; border: 1px solid #3a3028; position: relative; cursor: pointer; transition: background 0.3s; flex-shrink: 0; }
        .alert-toggle.on { background: rgba(200,169,110,0.3); border-color: #c8a96e; }
        .alert-toggle::after { content: ''; position: absolute; top: 2px; left: 2px; width: 14px; height: 14px; border-radius: 50%; background: #555; transition: transform 0.3s, background 0.3s; }
        .alert-toggle.on::after { transform: translateX(16px); background: #c8a96e; }
        .scan-btn { background: none; border: 1px solid #c8a96e; color: #c8a96e; cursor: pointer; font-family: 'Josefin Sans', sans-serif; font-size: 9px; letter-spacing: 3px; text-transform: uppercase; padding: 10px 24px; transition: all 0.3s; }
        .scan-btn:hover:not(:disabled) { background: rgba(200,169,110,0.1); }
        .scan-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .stat-card { background: linear-gradient(135deg, #1a1612, #141210); border: 1px solid #2a2520; padding: 18px 22px; border-radius: 2px; }
        @keyframes fadeInUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        @keyframes fadeIn   { from { opacity:0; } to { opacity:1; } }
        @keyframes slideUp  { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
        @keyframes slideDown { from { opacity:0; transform:translateY(-16px); } to { opacity:1; transform:translateY(0); } }
        @keyframes pulse-dot { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:.4; transform:scale(.7); } }
        @keyframes sweep { 0% { background-position:200% 0; } 100% { background-position:-200% 0; } }
        .fade-in { animation: fadeInUp 0.45s ease forwards; opacity:0; }
        .pulse-dot { animation: pulse-dot 2s ease-in-out infinite; }
        .skeleton { background: linear-gradient(90deg, #1a1612 25%, #252018 50%, #1a1612 75%); background-size:200% 100%; animation: sweep 1.4s infinite; }
      `}</style>

      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", backgroundImage: "radial-gradient(ellipse at 15% 15%, rgba(200,169,110,0.04) 0%, transparent 55%), radial-gradient(ellipse at 85% 85%, rgba(126,200,169,0.025) 0%, transparent 55%)", zIndex: 0 }} />

      {selectedBag && <Lightbox bag={selectedBag} onClose={() => setSelectedBag(null)} />}

      {notification && (
        <div style={{ position: "fixed", top: 24, right: 24, zIndex: 900, background: "#1a1612", border: "1px solid #c8a96e", padding: "14px 20px", maxWidth: 300, fontFamily: "'Josefin Sans', sans-serif", fontSize: 10, letterSpacing: 1.5, color: "#c8a96e", animation: "slideDown 0.35s ease", boxShadow: "0 8px 32px rgba(0,0,0,0.6)" }}>
          ◆ {notification}
        </div>
      )}

      {/* API error bar */}
      {apiError && !loading && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 800, background: "#1a0a0a", borderBottom: "1px solid #5a2020", padding: "10px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 9, letterSpacing: 2, color: "#c87070" }}>
            ⚠ &nbsp; Cannot reach bot — retrying every 60s
          </span>
          <button onClick={handleDisconnect} style={{ background: "none", border: "1px solid #5a2020", color: "#c87070", cursor: "pointer", fontFamily: "'Josefin Sans', sans-serif", fontSize: 8, letterSpacing: 2, padding: "4px 12px" }}>
            CHANGE URL
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ position: "fixed", inset: 0, zIndex: 700, background: "#0e0c0a", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 300, color: "#c8a96e", letterSpacing: 2 }}>Hermès Monitor</div>
          <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 9, letterSpacing: 4, color: "#444" }}>CONNECTING TO BOT…</div>
          <div style={{ width: 120, height: 1, background: "#2a2520", marginTop: 8, overflow: "hidden" }}>
            <div style={{ height: "100%", width: "40%", background: "linear-gradient(90deg, transparent, #c8a96e, transparent)", animation: "sweep 1.4s ease-in-out infinite", backgroundSize: "200% 100%" }} />
          </div>
        </div>
      )}

      <div style={{ maxWidth: 1140, margin: "0 auto", padding: `${apiError ? 64 : 40}px 24px 40px`, position: "relative", zIndex: 1 }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 48 }}>
          <div className="fade-in">
            <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 8, letterSpacing: 6, color: "#444", marginBottom: 10 }}>MAISON DE SURVEILLANCE</div>
            <h1 style={{ fontSize: 40, fontWeight: 300, letterSpacing: 2, lineHeight: 1, color: "#e8e0d4" }}>
              Hermès<br/><span style={{ color: "#c8a96e", fontStyle: "italic" }}>Monitor</span>
            </h1>
            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <div className="pulse-dot" style={{ width: 6, height: 6, borderRadius: "50%", background: apiError ? "#c87070" : scanning ? "#7ec8a9" : "#c8a96e" }} />
              <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 9, letterSpacing: 2, color: "#444" }}>
                {apiError ? "BOT OFFLINE" : scanning ? `SCANNING… ${Math.round(scanProgress)}%` : `LAST SCAN: ${lastScan}`}
              </span>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>
            <button className="scan-btn" onClick={triggerScan} disabled={scanning || apiError}>{scanning ? "Scanning..." : "Refresh Now"}</button>
            {scanning && (
              <div style={{ width: 140, height: 2, background: "#2a2520", borderRadius: 1, overflow: "hidden" }}>
                <div style={{ height: "100%", background: "linear-gradient(90deg, #c8a96e, #7ec8a9)", width: `${scanProgress}%`, transition: "width 0.2s ease" }} />
              </div>
            )}
            <button onClick={handleDisconnect} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'Josefin Sans', sans-serif", fontSize: 8, letterSpacing: 2, color: "#333" }}>
              CHANGE BOT URL
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 40 }}>
          {[
            { label: "Watching",      value: bags.length,    unit: "bags" },
            { label: "Available",     value: availableCount, unit: "now", accent: availableCount > 0 },
            { label: "Alerts On",     value: alertCount,     unit: "active" },
            { label: "Telegram Sent", value: alerts.filter(a => a.message?.includes("sent")).length, unit: "total" },
          ].map((stat, i) => (
            <div key={i} className="stat-card fade-in" style={{ animationDelay: `${i * 0.07}s` }}>
              <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 8, letterSpacing: 3, color: "#3a3028", marginBottom: 8 }}>{stat.label.toUpperCase()}</div>
              <div style={{ fontSize: 34, fontWeight: 300, color: stat.accent ? "#c8a96e" : "#e8e0d4", lineHeight: 1 }}>{stat.value}</div>
              <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 9, color: "#3a3028", marginTop: 4 }}>{stat.unit}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ borderBottom: "1px solid #1e1c18", marginBottom: 32, display: "flex" }}>
          {[{ key: "watchlist", label: "Watchlist" }, { key: "alerts", label: "Alert Log" }, { key: "settings", label: "Settings" }].map(t => (
            <button key={t.key} className={`tab-btn ${activeTab === t.key ? "active" : ""}`} onClick={() => setActiveTab(t.key)}>{t.label}</button>
          ))}
        </div>

        {/* WATCHLIST */}
        {activeTab === "watchlist" && (
          <div>
            {bags.length === 0 && !loading && (
              <div style={{ textAlign: "center", padding: "60px 0", fontFamily: "'Josefin Sans', sans-serif", fontSize: 10, letterSpacing: 3, color: "#333" }}>
                {apiError ? "BOT OFFLINE — CANNOT LOAD BAG DATA" : "WAITING FOR FIRST SCAN…"}
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
              {bags.map((bag, i) => {
                const s = statusConfig[bag.status] || statusConfig.unavailable;
                const ph = bag.priceHistory || [];
                const drop = ph.length >= 2 ? Math.round(((ph[0] - ph[ph.length-1]) / ph[0]) * 100) : 0;
                return (
                  <div key={bag.id} className={`bag-card ${bag.status} fade-in`} style={{ animationDelay: `${i * 0.06}s` }} onClick={() => setSelectedBag(bag)}>
                    <div className="photo-wrap">
                      {imageErrors[bag.id] ? (
                        <div className="skeleton" style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 8, letterSpacing: 2, color: "#3a3028" }}>NO IMAGE YET</span>
                        </div>
                      ) : (
                        <img src={getBagImage(bag)} alt={bag.name} onError={() => setImageErrors(p => ({ ...p, [bag.id]: true }))} />
                      )}
                      <div className="photo-overlay" />
                      <div className="photo-status" style={{ background: s.bg, border: `1px solid ${s.color}` }}>
                        <span style={{ color: s.color }}>{s.label}</span>
                      </div>
                    </div>

                    <div style={{ padding: "16px 18px 18px" }}>
                      <div style={{ fontSize: 19, fontWeight: 400, color: "#e8e0d4", marginBottom: 3 }}>{bag.name}</div>
                      <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 8, letterSpacing: 1.5, color: "#555", marginBottom: 12 }}>
                        {[bag.material, bag.color].filter(v => v && v !== "—").join(" · ") || "Details loading…"}
                      </div>

                      {bag.price ? (
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
                          <span style={{ fontSize: 22, fontWeight: 300, color: bag.status === "price_drop" ? "#7ec8a9" : "#c8a96e" }}>A${bag.price.toLocaleString()}</span>
                          {drop > 0 && <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 8, color: "#7ec8a9" }}>↓{drop}%</span>}
                        </div>
                      ) : (
                        <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 9, color: "#3a3028", letterSpacing: 2, marginBottom: 8 }}>PRICE LOADING…</div>
                      )}

                      {ph.length > 1 && (
                        <div style={{ display: "flex", gap: 3, alignItems: "flex-end", height: 18, marginBottom: 12 }}>
                          {ph.map((p, pi) => {
                            const max = Math.max(...ph);
                            const h = Math.max(4, Math.round((p / max) * 18));
                            return <div key={pi} style={{ flex: 1, height: h, background: pi === ph.length - 1 ? s.color : "#2a2520", borderRadius: 1 }} />;
                          })}
                        </div>
                      )}

                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 7.5, letterSpacing: 1, color: "#333" }}>{bag.lastSeen}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                          <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 7.5, letterSpacing: 1, color: "#333" }}>ALERT</span>
                          <div className={`alert-toggle ${bag.alertSet ? "on" : ""}`} onClick={e => toggleAlert(bag.id, e)} />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ALERTS */}
        {activeTab === "alerts" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {alerts.length === 0 && (
              <div style={{ padding: "60px 0", textAlign: "center", fontFamily: "'Josefin Sans', sans-serif", fontSize: 10, letterSpacing: 3, color: "#333" }}>
                NO ALERTS YET — BOT WILL LOG EVENTS HERE
              </div>
            )}
            {alerts.map((alert, i) => (
              <div key={i} className="fade-in" style={{ animationDelay: `${i * 0.04}s`, background: "#111009", border: "1px solid #1e1c18", padding: "14px 20px", display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: alert.message?.includes("ALERT") ? "#c8a96e" : "#2a2520" }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, color: "#e8e0d4", marginBottom: 3 }}>{alert.message}</div>
                  <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 8, letterSpacing: 2, color: "#3a3028" }}>{alert.time}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* SETTINGS */}
        {activeTab === "settings" && (
          <div style={{ maxWidth: 520, display: "flex", flexDirection: "column", gap: 28 }}>

            <div className="fade-in">
              <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 9, letterSpacing: 3, color: "#555", marginBottom: 12 }}>BOT API URL</div>
              <div style={{ background: "#111009", border: "1px solid #1e1c18", padding: "12px 16px", fontFamily: "'Josefin Sans', sans-serif", fontSize: 10, color: "#c8a96e", letterSpacing: 1, marginBottom: 8, wordBreak: "break-all" }}>
                {apiBase}
              </div>
              <button onClick={handleDisconnect} style={{ background: "none", border: "1px solid #2a2520", cursor: "pointer", padding: "8px 18px", color: "#555", fontFamily: "'Josefin Sans', sans-serif", fontSize: 9, letterSpacing: 2 }}>
                CHANGE URL
              </button>
            </div>

            <div className="fade-in" style={{ animationDelay: "0.1s" }}>
              <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 9, letterSpacing: 3, color: "#555", marginBottom: 12 }}>AUTO-REFRESH</div>
              <div style={{ background: "rgba(200,169,110,0.06)", border: "1px solid rgba(200,169,110,0.2)", padding: "12px 16px", display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 15 }}>Every 60 seconds</span>
                <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 8, color: "#c8a96e", letterSpacing: 2 }}>ACTIVE</span>
              </div>
            </div>

            <div className="fade-in" style={{ animationDelay: "0.2s", background: "#080706", border: "1px solid #1e1c18", padding: "18px 20px" }}>
              <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 9, letterSpacing: 3, color: "#555", marginBottom: 10 }}>API ENDPOINTS</div>
              {["/api/watchlist", "/api/alerts", "/health"].map(ep => (
                <div key={ep} style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 10, color: "#7ec8a9", marginBottom: 4 }}>{apiBase}{ep}</div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: 60, paddingTop: 24, borderTop: "1px solid #151310", display: "flex", justifyContent: "space-between", fontFamily: "'Josefin Sans', sans-serif", fontSize: 8, letterSpacing: 2, color: "#222" }}>
          <span>HERMÈS MONITOR · PERSONAL USE ONLY</span>
          <span>v3.0 · Auto-sync enabled</span>
        </div>
      </div>
    </div>
  );
}
